/**
 * Drag-and-drop + button upload for leave supporting documents (PDF/JPEG, max 10MB).
 * Stores S3 object key via presigned upload (same pipeline as SmartFileUpload).
 */

import { Button } from "@devhop/ui";
import { CloudUpload, Loader2, Trash2 } from "lucide-react";
import { useCallback, useId, useState } from "react";
import {
  DEFAULT_CHUNK_SIZE,
  uploadLargeFileInChunks,
} from "@/shared/lib/chunk-upload-s3";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { DEFAULT_LARGE_FILE_THRESHOLD_BYTES } from "@/shared/ui/SmartFileUpload";

const KEY_PREFIX = "uploads/hr/leave-requests";
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,application/pdf,image/jpeg";

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "file";
}

function validateSupportingDoc(file: File): string | null {
  if (file.size > MAX_BYTES) {
    return "ຂະໜາດຕ້ອງບໍ່ເກີນ 10MB";
  }
  const lower = file.name.toLowerCase();
  if (!/\.(pdf|jpe?g)$/.test(lower)) {
    return "ຮອງຮັບແຕ່ PDF ແລະ JPG";
  }
  return null;
}

export type LeaveRequestAttachmentUploadProps = {
  value: string | null;
  onChange: (key: string | null) => void;
  disabled?: boolean;
};

export function LeaveRequestAttachmentUpload({
  value,
  onChange,
  disabled = false,
}: LeaveRequestAttachmentUploadProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const buildKey = useCallback((file: File): string => {
    const ext = file.name.includes(".")
      ? (file.name.split(".").pop() ?? "")
      : "";
    const baseName = file.name.includes(".")
      ? file.name.slice(0, file.name.lastIndexOf("."))
      : file.name;
    const safeBase = sanitizeFilename(baseName);
    return `${KEY_PREFIX}/${Date.now()}-${safeBase}${ext ? `.${ext}` : ""}`;
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      setLocalError(null);
      const err = validateSupportingDoc(file);
      if (err) {
        setLocalError(err);
        return;
      }

      setUploading(true);
      setProgress(null);

      const isLarge = file.size > DEFAULT_LARGE_FILE_THRESHOLD_BYTES;

      try {
        if (isLarge) {
          setProgress({ loaded: 0, total: file.size });
          const key = await uploadLargeFileInChunks(file, {
            keyPrefix: KEY_PREFIX,
            chunkSize: DEFAULT_CHUNK_SIZE,
            onProgress: (loaded, total) => setProgress({ loaded, total }),
          });
          setProgress(null);
          onChange(key);
        } else {
          const key = buildKey(file);
          const contentType = file.type || undefined;
          const { uploadUrl } = await uploadApi.getPresignUrl({
            key,
            contentType,
          });
          await uploadFileToPresignedUrl(uploadUrl, file, { contentType });
          onChange(key);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setLocalError(msg);
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [buildKey, onChange],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void uploadFile(file);
      e.target.value = "";
    },
    [uploadFile],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !uploading) setDragOver(true);
    },
    [disabled, uploading],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (disabled || uploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) void uploadFile(file);
    },
    [disabled, uploading, uploadFile],
  );

  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.loaded / progress.total) * 100)
      : 0;

  const displayName = value?.split("/").pop() ?? null;

  return (
    <div className="space-y-2">
      <label className="font-medium text-sm" htmlFor={inputId}>
        ເອກະສານແນບ (ບໍ່ບັງຄັບ)
      </label>

      {value && !uploading && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
          <span className="break-all text-sm">{displayName}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1 text-destructive"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            <Trash2 className="h-4 w-4" />
            ລຶບ
          </Button>
        </div>
      )}

      <section
        aria-label="Upload supporting documents"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-8 text-center transition-colors",
          dragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25",
          disabled || uploading ? "pointer-events-none opacity-60" : "",
        ].join(" ")}
      >
        <input
          id={inputId}
          type="file"
          accept={ACCEPT_ATTR}
          className="sr-only"
          disabled={disabled || uploading}
          onChange={onInputChange}
        />
        {uploading ? (
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
        ) : (
          <CloudUpload
            className="h-10 w-10 text-muted-foreground"
            strokeWidth={1.25}
          />
        )}
        <div className="space-y-1">
          <p className="font-semibold text-sm">
            Upload medical certificates or documents
          </p>
          <p className="text-muted-foreground text-xs">PDF, JPG up to 10MB</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1 uppercase tracking-wide"
          disabled={disabled || uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading
            ? progress
              ? `ກຳລັງອັບໂຫຼດ... ${progressPercent}%`
              : "ກຳລັງອັບໂຫຼດ..."
            : "Select files"}
        </Button>
      </section>

      {progress && progress.total > 0 && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {localError && <p className="text-destructive text-sm">{localError}</p>}
    </div>
  );
}
